import React, { useRef, useState } from 'react';
import useChatStore from '../../stores/useChatStore';

export interface FlerpieFact {
  id: string;
  fact: string;
  created_at: string;
}

type Profile = { facts?: FlerpieFact[]; interests?: string[] | null } | null;

const OPEN_KEY = 'flerpie.memoryOpen';

const readOpen = () => {
  try {
    return localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
};

const joinTopics = (topics: string[]) => (topics.length < 2
  ? topics.join('')
  : `${topics.slice(0, -1).join(', ')} and ${topics[topics.length - 1]}`);

const FlerpieMemoryPanel = () => {
  const profile = useChatStore((s: { userProfile: Profile }) => s.userProfile);
  const [open, setOpen] = useState(readOpen);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [forgetting, setForgetting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const facts = (profile && profile.facts) || [];
  const interests = (profile && profile.interests) || [];

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(OPEN_KEY, next ? '1' : '0');
    } catch {
      // Private mode or a full quota: the panel still opens, it just will not stay open.
    }
    if (next) {
      requestAnimationFrame(() => {
        const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        sectionRef.current?.scrollIntoView?.({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
      });
    }
  };

  const add = async () => {
    const fact = draft.trim();
    if (!fact || saving) return;
    setSaving(true);
    setError(null);
    const ok = await useChatStore.getState().addFact(fact);
    setSaving(false);
    if (ok) setDraft('');
    else setError('That did not save. Try again in a moment.');
  };

  const forget = async (id: string) => {
    if (forgetting) return;
    setForgetting(id);
    setError(null);
    const ok = await useChatStore.getState().forgetFact(id);
    setForgetting(null);
    if (!ok) setError('That did not forget. Try again in a moment.');
  };

  return (
    <section
      ref={sectionRef}
      className="mx-1.5 mt-auto mb-1.5 pt-2 flex-shrink-0"
    >
      <div className="rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls="flerpie-memory-body"
          className="np-state w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-left
                     outline-none focus-visible:ring-1 focus-visible:ring-primary/40"
        >
          <span className="text-[11px] text-gray-400 truncate">What Flerpie remembers about you</span>
          <svg
            width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"
            className={`flex-shrink-0 text-gray-500 transition-transform duration-200 motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}
          >
            <path d="M3.2 1.2L7.6 5L3.2 8.8L2.2 7.7L5.4 5L2.2 2.3Z" fill="currentColor" />
          </svg>
        </button>

        {open && (
          <div id="flerpie-memory-body" className="px-2.5 pb-2.5 flex flex-col gap-2">
            {interests.length > 0 && (
              <p className="text-[10px] text-gray-500 m-0">Lately you ask mostly about {joinTopics(interests)}.</p>
            )}

            {profile === null && <p className="text-[11px] text-gray-500 m-0">Loading...</p>}

            {profile !== null && facts.length === 0 && (
              <p className="text-[11px] text-gray-500 m-0">Nothing yet. Tell him something about yourself, or add it here.</p>
            )}

            {facts.length > 0 && (
              <ul className="flex flex-col gap-1.5 m-0 p-0 list-none">
                {facts.map((f) => (
                  <li key={f.id} className="flex items-start gap-2">
                    <span className="flex-1 min-w-0 text-[11px] text-gray-300 leading-snug break-words">{f.fact}</span>
                    <button
                      type="button"
                      onClick={() => forget(f.id)}
                      disabled={forgetting !== null}
                      aria-label={`Forget: ${f.fact}`}
                      className="flex-shrink-0 text-[10px] text-gray-500 hover:text-red-300 focus-visible:text-red-300
                                 outline-none px-1 -mr-1 py-0.5 rounded disabled:opacity-50"
                    >
                      {forgetting === f.id ? 'Forgetting...' : 'Forget'}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
              readOnly={saving}
              maxLength={160}
              placeholder="Something he should remember"
              aria-label="Something he should remember"
              className="w-full m-0 rounded-md px-2 py-1.5 text-[11px] text-gray-200 placeholder:text-gray-600
                         outline-none focus-visible:ring-1 focus-visible:ring-primary/40"
              style={{ background: 'rgba(0,0,0,0.25)', boxShadow: 'inset 0 1px 2px rgba(0,0,0,.35)' }}
            />
            {(saving || draft.trim()) && !error && (
              <p className="text-[10px] text-gray-600 m-0 -mt-1">{saving ? 'Saving...' : 'Enter to save.'}</p>
            )}
            {error && <p className="text-[10px] text-red-300 m-0 -mt-1">{error}</p>}
          </div>
        )}
      </div>
    </section>
  );
};

export default FlerpieMemoryPanel;
